// Microbiome Foundations — sister course to The Hormone-Gut Axis.
// Source: Murray, Rosenthal, Pfaller — Medical Microbiology, 9th ed
// (Elsevier), supplemented by peer-reviewed research and integrative
// (Ayurveda / TCM / Functional Medicine) sources curated in the Library.
//
// Lesson IDs are prefixed "mb" to avoid collision with the hormone
// course's "1a"/"2a" ids in the shared LESSON_INDEX.
//
// Design rule: short body text (one hook line) — diagrams are the
// centrepiece. Lessons follow the same diagram-led pattern as the
// existing Hormone-Gut Axis course.

export const MICROBIOME_COURSE = {
  slug: "microbiome",
  title: "Microbiome Foundations",
  subtitle: "From microbe to medicine — the science of the human ecosystem",
  sourceNote:
    "Built from Murray, Rosenthal & Pfaller — Medical Microbiology, 9th ed (Elsevier) — and peer-reviewed research curated in the Library, including Ayurvedic and Chinese-medicine perspectives.",
  modules: [
    // -------------------------------------------------------------------
    // MODULE 1 — Meet the Microbiome  (FULLY WRITTEN)
    // -------------------------------------------------------------------
    {
      id: 1,
      title: "Meet the Microbiome",
      icon: "🌍",
      colorVar: "--mod-1",
      summary:
        "What the human microbiome actually is, where its members live, and how we tell friend from foe.",
      lessons: [
        {
          id: "mb1.1",
          title: "What the Microbiome Actually Is",
          duration: "7 min",
          content: {
            intro:
              "You are outnumbered. By cells, your body is roughly half human and half microbial. By genes, the bacteria living inside you outnumber your own ~150-fold. The microbiome isn't a side topic in medicine — it's a second organ.",
            heroImage: {
              src: "/diagrams/mb1-1-microbiome-overview.png",
              alt: "Illustration of the human microbiome showing the five major bacterial phyla surrounding a human silhouette",
              caption:
                "The five major bacterial phyla of the human gut, mapped against the host. Together they outnumber human cells roughly 1:1 and outnumber human genes ~150-fold.",
              credit: "Illustration: generated for Hikari Labs",
            },
            sections: [
              {
                heading: "Vocabulary that matters",
                body: "Three words used interchangeably in popular writing — each means something genuinely different.",
                diagram: {
                  type: "cards",
                  items: [
                    { label: "Microbiota", sub: "The organisms", desc: "The actual living cells — bacteria, archaea, fungi, viruses, occasional protozoa. Counted by 16S rRNA sequencing.", colorVar: "--mod-1" },
                    { label: "Microbiome", sub: "Organisms + genes + niche", desc: "The full ecosystem: the microbes, their collective gene pool, and the environment they inhabit (gut lumen, skin, vagina, etc.).", colorVar: "--mod-6" },
                    { label: "Metagenome", sub: "All genes, all sources", desc: "Total DNA in a sample, microbial AND host. Analysed by shotgun sequencing to ask 'what can they do', not just 'who's there'.", colorVar: "--mod-2" },
                    { label: "Holobiont", sub: "Host + microbes as one unit", desc: "Emerging concept: you-plus-your-microbes function as a single organism, and evolution has selected on this combined unit (Bordenstein & Theis, 2015).", colorVar: "--mod-8" },
                  ],
                },
              },
              {
                heading: "The scale, properly understood",
                body: "Forget the old 10:1 ratio. The modern number is closer to 1:1 — but the throughput is what matters.",
                diagram: {
                  type: "cards",
                  items: [
                    { label: "~38 trillion bacteria", sub: "Roughly 1:1 with human cells", desc: "Old '10:1' estimate revised by Sender, Fuchs & Milo (Cell, 2016). About 0.2 kg of you, by mass, is bacteria — mostly colonic.", colorVar: "--mod-1" },
                    { label: "~150× more genes", sub: "Than the human genome", desc: "Your ~23,000 protein-coding genes are dwarfed by the ~3.3 million distinct microbial genes catalogued in the gut (MetaHIT consortium).", colorVar: "--mod-6" },
                    { label: "500–1,000 species per person", sub: "Highly individual", desc: "Two healthy adults often share less than half their species. A 'healthy microbiome' is a functional profile, not a fixed roster.", colorVar: "--mod-2" },
                    { label: "Built in the first 3 years", sub: "Then stable for decades", desc: "Birth mode, breastfeeding, antibiotics and weaning shape the adult microbiome. By age 3 it resembles an adult's, and stays stable unless disrupted.", colorVar: "--mod-3" },
                  ],
                },
              },
              {
                heading: "The major bacterial phyla",
                body: "Almost the entire gut belongs to one of five phyla. Learn these names and dense papers turn readable.",
                diagram: {
                  type: "probiotic-table",
                  items: [
                    { bacteria: "Firmicutes", role: "Largest phylum by species. Includes Lactobacillus, Clostridium, Faecalibacterium, Roseburia. Many are key butyrate producers — the gut's preferred fuel.", source: "Increased on Western/high-fat diets; reduced in IBD" },
                    { bacteria: "Bacteroidetes", role: "Second-largest. Includes Bacteroides and Prevotella. Specialists in breaking down complex plant fibres into short-chain fatty acids.", source: "Increased on high-fibre/Mediterranean diets; reduced on Western diets" },
                    { bacteria: "Actinobacteria", role: "Includes Bifidobacterium (the psychobiotic strains live here) and Collinsella. Dominant in breastfed infants.", source: "Declines with age and antibiotic exposure" },
                    { bacteria: "Proteobacteria", role: "Small but informative. Includes E. coli, Salmonella, Helicobacter. Inflammation and dysbiosis tend to expand this group.", source: "Rises in IBD, metabolic disease, after antibiotics" },
                    { bacteria: "Verrucomicrobia", role: "Tiny phylum — almost entirely Akkermansia muciniphila, a mucus-loving species that strengthens the gut barrier.", source: "Reduced in obesity, T2D, and inflammation" },
                  ],
                },
              },
              {
                heading: "How we know what's in there",
                body: "Most gut bacteria refuse to grow in a petri dish. Three modern technologies cracked that open.",
                diagram: {
                  type: "two-column",
                  left: {
                    title: "16S rRNA sequencing",
                    colorVar: "--mod-1",
                    points: [
                      "Cheap, fast, the workhorse",
                      "Sequences one ribosomal gene present in all bacteria",
                      "Tells you 'who is there' to genus or species level",
                      "Can't tell you what they're doing",
                      "Used in most consumer microbiome reports",
                    ],
                  },
                  right: {
                    title: "Shotgun metagenomics + culturomics",
                    colorVar: "--mod-6",
                    points: [
                      "Shotgun: sequences ALL DNA in the sample",
                      "Tells you function — enzymes, pathways, resistance genes",
                      "Required for serious functional research",
                      "Culturomics: hundreds of carefully optimised media",
                      "Re-grew species long thought 'unculturable' (Lagier et al., 2018)",
                    ],
                  },
                },
              },
            ],
            flashcards: [
              { q: "Define microbiota, microbiome and metagenome — what's the difference?", a: "Microbiota = the organisms themselves. Microbiome = organisms + their collective genes + their environment. Metagenome = all DNA in a sample, microbial AND host." },
              { q: "What's the modern best estimate of the bacteria-to-human-cell ratio?", a: "Roughly 1:1 (~38 trillion bacteria to ~30 trillion human cells), per Sender, Fuchs & Milo (Cell, 2016). The old '10:1' figure was wrong." },
              { q: "Name the five major bacterial phyla in the human gut.", a: "Firmicutes, Bacteroidetes, Actinobacteria, Proteobacteria, and Verrucomicrobia. Firmicutes and Bacteroidetes dominate by abundance; Verrucomicrobia is almost entirely Akkermansia muciniphila." },
              { q: "Why does shotgun metagenomics complement 16S sequencing?", a: "16S tells you WHO is present (taxonomic identity). Shotgun sequences ALL DNA and tells you WHAT they can do (functional genes, pathways, antibiotic resistance). You need both for serious research." },
            ],
          },
        },
        {
          id: "mb1.2",
          title: "A Body-System Tour",
          duration: "8 min",
          content: {
            intro:
              "You don't have 'a' microbiome — you have several. Skin, mouth, nose, stomach, small intestine, colon, vagina: each is its own ecosystem, with its own dominant species and its own role in health.",
            heroImage: {
              src: "/diagrams/mb1-2-body-tour.png",
              alt: "Anatomical illustration of distinct microbial neighbourhoods across the human body",
              caption:
                "Skin, mouth, stomach, small intestine, colon and vagina each select for radically different communities — two patches of skin centimetres apart can look like different planets.",
              credit: "Illustration: generated for Hikari Labs",
            },
            sections: [
              {
                heading: "The body's distinct neighbourhoods",
                body: "Different sites select for radically different microbes. Two patches of skin centimetres apart can look like different planets (Costello, Science 2009).",
                diagram: {
                  type: "symptom-grid",
                  items: [
                    { system: "Skin", symptoms: ["Cutibacterium acnes (sebaceous areas)", "Staphylococcus epidermidis (everywhere)", "Corynebacterium (moist areas — armpits, groin)", "Malassezia yeasts", "Dry, salty, aerobic — selects for tough survivors"] },
                    { system: "Oral cavity", symptoms: ["Streptococcus species (most diverse here)", "Veillonella, Neisseria, Haemophilus", "~700 species — second most diverse site after gut", "Plaque is a highly structured biofilm", "Periodontal disease links to systemic inflammation"] },
                    { system: "Stomach", symptoms: ["Very low diversity — pH ~2 kills most things", "Helicobacter pylori is the famous survivor", "Uses urease to neutralise acid locally", "Present in ~50% of humans, mostly asymptomatic", "When pathogenic: ulcers, gastric cancer"] },
                    { system: "Small intestine", symptoms: ["Fast transit (~3–5 hours)", "Some oxygen present near the wall", "Streptococcus, Lactobacillus, facultative anaerobes", "Bacterial density rises distally", "SIBO = overgrowth here — colon bacteria where they don't belong"] },
                    { system: "Colon (large intestine)", symptoms: ["70–80% of body's total microbiome lives here", "Strict anaerobic environment", "Bacteroides, Faecalibacterium, Roseburia, Akkermansia, Bifidobacterium", "Where SCFAs (butyrate, propionate, acetate) are produced", "Site of most diversity, drug metabolism, and immune training"] },
                    { system: "Vagina", symptoms: ["Lactobacillus-dominated in health (esp. L. crispatus, L. iners)", "Lactobacilli produce lactic acid → pH ~3.5–4.5", "Five 'community state types' identified", "Bacterial vaginosis = shift away from Lactobacillus dominance", "Shifts with cycle, pregnancy, and menopause"] },
                  ],
                },
              },
              {
                heading: "Deep dive: the gut",
                body: "The journey through the GI tract is one of progressively higher density and lower oxygen.",
                diagram: {
                  type: "flow",
                  items: [
                    { icon: "👄", label: "Mouth", note: "~10⁹ bacteria/mL saliva. Second-most diverse site." },
                    { icon: "🫙", label: "Stomach (pH ~2)", note: "Low diversity. H. pylori survives via urease." },
                    { icon: "🌀", label: "Small intestine", note: "~10⁴–10⁸/g. Some oxygen, fast transit. SIBO territory." },
                    { icon: "🦠", label: "Colon", note: "~10¹¹/g. Strict anaerobes; the metabolic powerhouse." },
                    { icon: "💩", label: "Stool", note: "~50% of solid mass is bacteria (live + dead)." },
                  ],
                },
              },
              {
                heading: "Sites that matter beyond the gut",
                body: "Other body sites carry oversized weight for hormones, immunity and travel — we'll return to each.",
                diagram: {
                  type: "cards",
                  items: [
                    { label: "Vaginal microbiome", sub: "Lactobacillus-dominated", desc: "Healthy state: L. crispatus or L. iners dominant. Disruption (BV) raises STI/preterm-birth risk and links to gut estrobolome shifts.", colorVar: "--mod-5" },
                    { label: "Oral microbiome", sub: "~700 species", desc: "Periodontal pathogens (P. gingivalis) have been found inside atherosclerotic plaques. Daily oral hygiene is a cardiovascular intervention.", colorVar: "--mod-3" },
                    { label: "Skin microbiome", sub: "Gut-skin axis", desc: "Eczema, acne and rosacea all linked to gut dysbiosis. We'll return to this in Module 8.", colorVar: "--mod-7" },
                    { label: "Respiratory microbiome", sub: "Discovered late", desc: "The lung was thought sterile until ~2010. Composition predicts asthma exacerbations and COPD progression.", colorVar: "--mod-8" },
                  ],
                },
              },
            ],
            flashcards: [
              { q: "Where does the majority of the human microbiome live, and roughly how much of it is there?", a: "The colon, by a large margin — about 70–80% of total microbial biomass. Density reaches ~10¹¹ bacteria per gram of colonic contents (about 0.2 kg of bacteria total)." },
              { q: "How does the small intestine differ from the colon as a microbial habitat?", a: "Faster transit (~3–5 hours), some oxygen present, lower density (~10⁴–10⁸/g vs 10¹¹/g), and dominated by facultative anaerobes like Streptococcus and Lactobacillus. SIBO = overgrowth into this zone." },
              { q: "What dominates a healthy vaginal microbiome, and why?", a: "Lactobacillus species — especially L. crispatus and L. iners. They produce lactic acid, keeping pH at ~3.5–4.5, which suppresses pathogens. Disruption (BV) raises infection and preterm-birth risk." },
              { q: "Name two body sites whose microbiome was discovered to be NOT sterile, against earlier dogma.", a: "The lung (thought sterile until ~2010) and the placenta/intrauterine environment (still debated — but increasingly evidence of a low-biomass community)." },
            ],
          },
        },
        {
          id: "mb1.3",
          title: "Friend, Foe, Opportunist — How Microbes Become Pathogens",
          duration: "7 min",
          content: {
            intro:
              "The instinct to sort microbes into 'good' and 'bad' is wrong. Almost every organism in your gut sits on a spectrum that depends on context — what else is there, what your immune system is doing, what your barrier looks like.",
            heroImage: {
              src: "/diagrams/mb1-3-pathogen-spectrum.png",
              alt: "Spectrum diagram of host-microbe relationships from commensal to pathogen",
              caption:
                "The commensal-to-pathogen spectrum. The same organism can sit at different positions depending on barrier integrity, immune state and ecological context.",
              credit: "Illustration: generated for Hikari Labs",
            },
            sections: [
              {
                heading: "The four relationship types (plus one)",
                body: "Modern microbiology recognises four classic types — plus a critical fifth that doesn't fit either box.",
                diagram: {
                  type: "cards",
                  items: [
                    { label: "Commensal", sub: "Lives with us, no measurable effect", desc: "Latin: 'sharing the table'. Doesn't help, doesn't hurt — at least detectably. Most of your microbiome is technically commensal.", colorVar: "--mod-2" },
                    { label: "Mutualist", sub: "Both benefit", desc: "True partnership — microbe gets habitat and food, we get vitamin K, SCFAs, immune training, colonisation resistance. Bacteroides species are textbook mutualists.", colorVar: "--mod-1" },
                    { label: "Pathogen", sub: "Causes disease in a healthy host", desc: "Carries the machinery (adhesins, toxins, immune evasion) to cause disease even in immunocompetent people. Salmonella, Shigella, Vibrio cholerae.", colorVar: "--danger" },
                    { label: "Opportunist", sub: "Pathogenic only in compromised hosts", desc: "Harmless or marginal in health, dangerous when host is weakened — immunosuppression, antibiotic disruption, broken barrier. Candida albicans is the classic.", colorVar: "--mod-3" },
                    { label: "Pathobiont", sub: "Member-turned-villain", desc: "A resident commensal that turns harmful when context shifts (dysbiosis, inflammation, barrier breach). Often the most clinically relevant category. Bacteroides fragilis is either, depending on where it sits.", colorVar: "--mod-5" },
                  ],
                },
              },
              {
                heading: "What makes a pathogen pathogenic",
                body: "Disease isn't just 'the microbe is there'. Pathogens carry a toolkit of virulence factors — different combinations cause different diseases.",
                diagram: {
                  type: "cause-web",
                  items: [
                    { icon: "🪝", label: "Adhesins", note: "Surface molecules that stick to host receptors (E. coli pili, FimH)" },
                    { icon: "☠️", label: "Toxins", note: "Cholera toxin, diphtheria toxin, C. difficile toxins A and B — most damage cascades from these" },
                    { icon: "🛡️", label: "Capsules", note: "Polysaccharide coats that block phagocytosis (Streptococcus pneumoniae)" },
                    { icon: "💉", label: "Secretion systems", note: "Type III (Salmonella) and Type IV (Helicobacter) injectors that deliver effectors into host cells" },
                    { icon: "🧬", label: "Immune evasion", note: "Antigenic variation (Neisseria), molecular mimicry, intracellular hiding" },
                    { icon: "🍳", label: "Acquired nutrition", note: "Iron-scavenging (siderophores), exploitation of inflammatory by-products" },
                    { icon: "🧲", label: "Quorum sensing", note: "Cell-cell signalling that coordinates attack only above a critical density" },
                  ],
                },
              },
              {
                heading: "When opportunists strike",
                body: "Most modern infections aren't from incoming pathogens — they're from organisms already on you, taking a momentary opening.",
                diagram: {
                  type: "pathway",
                  steps: [
                    { num: "1", label: "Baseline state", body: "Commensal or pathobiont living quietly. Held in check by a healthy barrier, competing microbes, and an alert immune system.", colorVar: "--mod-2" },
                    { num: "2", label: "Antibiotic disruption", body: "Broad-spectrum antibiotics wipe out competitors. Niche opens. Classic precursor to C. difficile colitis and post-antibiotic Candida overgrowth.", colorVar: "--mod-3" },
                    { num: "3", label: "Immune suppression", body: "Chemo, steroids, HIV, severe stress. The standing surveillance falls. Opportunists previously held in check expand.", colorVar: "--mod-5" },
                    { num: "4", label: "Barrier breach", body: "Surgical wound, IV catheter, severe burn, leaky gut. Skin/mucosal microbes find themselves in tissue they're not adapted to.", colorVar: "--danger" },
                    { num: "5", label: "Infection", body: "What was a commensal becomes a pathogen. The microbe didn't change — the conditions did.", colorVar: "--mod-1" },
                  ],
                },
              },
            ],
            flashcards: [
              { q: "Define 'pathobiont' and give a clinical example.", a: "A resident commensal that can become pathogenic when host context shifts (dysbiosis, inflammation, barrier breach). Classic example: Bacteroides fragilis is a normal gut commensal but causes serious abscesses when it crosses into the peritoneum after a perforation." },
              { q: "What are Falkow's molecular Koch's postulates?", a: "Stanley Falkow's 1988 update to Koch's 19th-century criteria. To declare a microbial gene as the cause of a virulence trait: (1) the gene must be present in pathogenic strains, (2) disrupting it must abolish virulence, (3) restoring it must restore virulence. Moved disease attribution from species to specific genes." },
              { q: "Name four major virulence-factor categories that distinguish pathogens from commensals.", a: "Adhesins (stick to host), toxins (damage host), capsules / immune-evasion molecules (avoid being killed), and secretion systems (inject effectors into host cells). Quorum sensing and iron-scavenging round out the toolkit." },
              { q: "Why are post-antibiotic C. difficile and Candida overgrowth such common opportunistic infections?", a: "Broad-spectrum antibiotics wipe out the competing commensals that normally keep them in check. The niche opens, and the opportunist — already present at low levels — expands unchallenged. Context, not pathogen, determines disease." },
            ],
          },
        },
      ],
    },

    // -------------------------------------------------------------------
    // MODULE 2 — How Bacteria Work (stub)
    // -------------------------------------------------------------------
    {
      id: 2,
      title: "How Bacteria Work",
      icon: "🧬",
      colorVar: "--mod-2",
      summary:
        "Anatomy, metabolism, and the beneficial bacteria that produce short-chain fatty acids and the estrobolome.",
      comingSoon: true,
      lessons: [],
    },

    // -------------------------------------------------------------------
    // MODULE 3 — The Immune Conversation (stub)
    // -------------------------------------------------------------------
    {
      id: 3,
      title: "The Immune Conversation",
      icon: "🛡️",
      colorVar: "--mod-3",
      summary:
        "The gut barrier, innate and adaptive immunity, oral tolerance, and how the gut talks to the immune system.",
      comingSoon: true,
      lessons: [],
    },

    // -------------------------------------------------------------------
    // MODULE 4 — Dysbiosis & Disease (stub)
    // -------------------------------------------------------------------
    {
      id: 4,
      title: "Dysbiosis & Disease",
      icon: "⚠️",
      colorVar: "--mod-4",
      summary:
        "Pathogenesis, gut overgrowths (SIBO, H. pylori, C. diff), and Candida / fungal dysbiosis.",
      comingSoon: true,
      lessons: [],
    },

    // -------------------------------------------------------------------
    // MODULE 5 — Travel & the Microbiome (stub)
    // -------------------------------------------------------------------
    {
      id: 5,
      title: "Travel & the Microbiome",
      icon: "✈️",
      colorVar: "--mod-5",
      summary:
        "Before, during and after international travel — pathogens, post-infectious IBS, helminth research, and microbiome recovery.",
      comingSoon: true,
      lessons: [],
    },

    // -------------------------------------------------------------------
    // MODULE 6 — Gut, Hormones & Mood  (FLAGSHIP — FULLY WRITTEN)
    // -------------------------------------------------------------------
    {
      id: 6,
      title: "Gut, Hormones & Mood",
      icon: "🧠",
      colorVar: "--mod-6",
      summary:
        "The flagship module. Seven lessons connecting the enteric nervous system, neurotransmitters, inflammation, the HPA axis, estrogen, psychobiotics and Eastern frameworks.",
      lessons: [
        {
          id: "mb6.1",
          title: "The Gut as Your Second Brain",
          duration: "7 min",
          content: {
            intro:
              "Your gut has its own brain. Around 500 million neurons — more than your spinal cord — wrap your digestive tract in a network called the enteric nervous system. It doesn't just digest food. It talks to your actual brain, both directions, every minute.",
            heroImage: {
              src: "/diagrams/mb6-1-vagus-nerve.png",
              alt: "Anatomical illustration of the vagus nerve and enteric nervous system",
              caption:
                "The vagus nerve (cranial nerve X) connects brainstem to gut. 80–90% of its fibres carry traffic gut → brain. Inset: the enteric nervous system wrapping the bowel wall.",
              credit: "Illustration: generated for Hikari Labs",
            },
            sections: [
              {
                heading: "The Enteric Nervous System (ENS)",
                body: "The only peripheral nervous system that can run independently. Cut its link to the brain — the gut still works.",
                diagram: {
                  type: "cards",
                  items: [
                    { label: "~500 million neurons", sub: "More than your spinal cord", desc: "Embedded in the wall of the gut from oesophagus to rectum, organised into two plexuses (myenteric and submucosal).", colorVar: "--mod-6" },
                    { label: "30+ neurotransmitters", sub: "Same chemicals as the brain", desc: "Serotonin, dopamine, GABA, acetylcholine, nitric oxide — produced both by your own neurons AND by gut microbes.", colorVar: "--mod-1" },
                    { label: "Operates independently", sub: "Functions without brain input", desc: "The ENS is the only peripheral system that runs reflex arcs entirely on its own — it can digest, sense and respond even if the vagus is severed.", colorVar: "--mod-2" },
                    { label: "~100 trillion microbes", sub: "Adding their own signals", desc: "Resident bacteria don't just live in the gut — they produce molecules that talk directly to ENS neurons.", colorVar: "--mod-8" },
                  ],
                },
              },
              {
                heading: "The Vagus Nerve: Information Superhighway",
                body: "If the ENS is the gut's local brain, the vagus is its phone line. The gut talks far more than the brain replies.",
                diagram: {
                  type: "flow",
                  items: [
                    { icon: "🧠", label: "Brainstem", note: "Vagal nucleus — the gut's destination in the brain" },
                    { icon: "🔌", label: "Vagus nerve", note: "Cranial nerve X — 80–90% of fibres are afferent (gut → brain)" },
                    { icon: "🫁", label: "Through chest", note: "Branches to heart and lungs along the way" },
                    { icon: "🌿", label: "ENS sensory neurons", note: "Detect stretch, chemicals, microbial metabolites" },
                    { icon: "🦠", label: "Microbiome", note: "Bacteria produce molecules that activate ENS neurons" },
                  ],
                },
              },
              {
                heading: "Four channels of conversation",
                body: "At least four parallel routes connect gut and brain. They overlap — which is why a single probiotic can ripple across mood, digestion and immunity.",
                diagram: {
                  type: "pathway",
                  steps: [
                    { num: "1", label: "Vagal afferents", body: "Fast, electrical. The literal 'gut feeling'. Mediates butterflies-in-the-stomach in real time.", colorVar: "--mod-6" },
                    { num: "2", label: "Microbial neurotransmitters", body: "Gut bacteria synthesise serotonin, GABA, dopamine. Their molecules cross or signal across the gut barrier.", colorVar: "--mod-1" },
                    { num: "3", label: "Immune cytokines", body: "Gut-associated lymphoid tissue (GALT) releases cytokines that reach the brain via blood. Underlies sickness-behaviour and inflammatory mood symptoms.", colorVar: "--mod-3" },
                    { num: "4", label: "Bacterial metabolites", body: "Short-chain fatty acids (butyrate, propionate, acetate) cross the blood-brain barrier and modulate microglia.", colorVar: "--mod-2" },
                    { num: "5", label: "HPA axis loop", body: "Stress hormones close the loop — they alter gut permeability, and the microbiome responds back to them.", colorVar: "--mod-4" },
                  ],
                },
              },
            ],
            flashcards: [
              { q: "How many neurons does the enteric nervous system contain?", a: "Around 500 million — more than your spinal cord. It runs on 30+ neurotransmitters." },
              { q: "What percentage of vagus nerve fibres carry signals FROM the gut TO the brain?", a: "About 80–90%. Your gut talks to your brain far more than the brain replies." },
              { q: "Name the four major channels of gut–brain communication.", a: "Vagal afferents, microbial neurotransmitters, immune cytokines, and bacterial metabolites (SCFAs) — with the HPA axis as a closing feedback loop." },
              { q: "Who coined the term 'second brain' for the enteric nervous system?", a: "Michael Gershon, neurogastroenterologist — in his 1998 book of the same name." },
            ],
          },
        },
        {
          id: "mb6.2",
          title: "The Neurotransmitter Factory",
          duration: "8 min",
          content: {
            intro:
              "About 95% of your serotonin is made in your gut, not your brain. The microbes that live there don't just sit passively — they manufacture, modulate and consume the molecules your nervous system runs on.",
            heroImage: {
              src: "/diagrams/mb6-2-tryptophan-pathways.png",
              alt: "Biochemical pathway diagram of tryptophan metabolism into serotonin or kynurenine",
              caption:
                "Tryptophan's fork. IDO — activated by inflammatory cytokines (IL-6, TNF-α) — diverts tryptophan from the serotonin path into the kynurenine path, producing neurotoxic metabolites.",
              credit: "Illustration: generated for Hikari Labs",
            },
            sections: [
              {
                heading: "Where mood-chemicals are actually made",
                body: "Different strains specialise in different neurotransmitters — which is why probiotic effects on mood are strain-specific.",
                diagram: {
                  type: "probiotic-table",
                  items: [
                    { bacteria: "Enterochromaffin cells (host)", role: "Produce ~95% of body serotonin — induced by signals from spore-forming gut bacteria (Yano et al., 2015)", source: "The gut lining; activity tunable by microbiome composition" },
                    { bacteria: "Lactobacillus rhamnosus JB-1", role: "Increases GABA-A receptor expression; reduces anxiety/depression-like behaviour via the vagus nerve (Bravo et al., 2011)", source: "Yoghurt, kefir, specific probiotic supplements" },
                    { bacteria: "Bifidobacterium longum 1714", role: "Produces GABA; reduces stress reactivity in healthy volunteers (Allen et al., 2016)", source: "Fermented dairy; PrecisionBiotics Zenflore" },
                    { bacteria: "L. helveticus R0052 + B. longum R0175", role: "Reduces anxiety and depression scores in double-blind RCT (Messaoudi et al., 2011) — the canonical 'psychobiotic'", source: "Targeted psychobiotic supplements (Cerebiome)" },
                    { bacteria: "Escherichia coli (commensal strains)", role: "Produces dopamine and noradrenaline precursors", source: "Resident in healthy gut at low population levels" },
                  ],
                },
              },
              {
                heading: "Tryptophan: the master mood molecule",
                body: "Tryptophan can go down two routes — and your microbes decide which. Inflammation tips the balance away from serotonin.",
                diagram: {
                  type: "pathway",
                  steps: [
                    { num: "1", label: "Dietary tryptophan", body: "From eggs, turkey, oats, dairy, seeds. Absorbed in the small intestine.", colorVar: "--mod-2" },
                    { num: "2", label: "The fork", body: "~95% goes to kynurenine pathway; ~5% to serotonin. Inflammation tips even more toward kynurenine.", colorVar: "--mod-3" },
                    { num: "3a", label: "Serotonin path", body: "Tryptophan → 5-HTP → serotonin → melatonin. Supports mood, sleep, gut motility.", colorVar: "--mod-6" },
                    { num: "3b", label: "Kynurenine path", body: "Driven by IDO enzyme (activated by inflammation). Produces quinolinic acid (neurotoxic) and kynurenic acid.", colorVar: "--danger" },
                    { num: "4", label: "Microbial regulation", body: "Lactobacilli and Bifidobacteria produce indoles that REDUCE IDO and protect serotonin output.", colorVar: "--mod-1" },
                  ],
                },
              },
              {
                heading: "Why gut serotonin matters for mood",
                body: "Serotonin itself doesn't cross the blood-brain barrier. Its precursors do — and the gut's inflammatory state decides how much gets through.",
                diagram: {
                  type: "definition-box",
                  term: "Psychobiotic",
                  definition: "A live organism that, when ingested in adequate amounts, produces a health benefit in patients suffering from psychiatric illness (Dinan, Stanton & Cryan, 2013).",
                  key_enzyme: "Indoleamine 2,3-dioxygenase (IDO) — the gatekeeper enzyme between serotonin and kynurenine pathways",
                  key_bacteria: ["Lactobacillus helveticus R0052", "Bifidobacterium longum R0175", "L. rhamnosus JB-1", "B. longum 1714"],
                },
              },
            ],
            flashcards: [
              { q: "What percentage of body serotonin is made in the gut?", a: "Approximately 95%. It's produced by enterochromaffin cells, induced by signals from spore-forming gut bacteria." },
              { q: "Which two strains made up the canonical 'psychobiotic' RCT of Messaoudi et al. (2011)?", a: "Lactobacillus helveticus R0052 and Bifidobacterium longum R0175 — together marketed as Cerebiome." },
              { q: "What is the kynurenine pathway and why does it matter for mood?", a: "An alternative route for tryptophan metabolism, activated by inflammation. It diverts tryptophan away from serotonin production toward neurotoxic metabolites — a proposed mechanism of inflammation-driven depression." },
              { q: "Which enzyme is the gatekeeper between the serotonin and kynurenine pathways?", a: "Indoleamine 2,3-dioxygenase (IDO). Inflammatory cytokines activate it, pushing tryptophan toward kynurenine and away from serotonin." },
            ],
          },
        },
        {
          id: "mb6.3",
          title: "Inflammation → Depression",
          duration: "8 min",
          content: {
            intro:
              "Depression isn't only a chemical-imbalance story. A growing body of research treats it as an inflammatory illness — and the gut is one of the biggest sources of inflammation in the body.",
            heroImage: {
              src: "/diagrams/mb6-3-leaky-gut-inflammation.png",
              alt: "Cross-section of the intestinal epithelium showing leaky gut and downstream neuroinflammation",
              caption:
                "Top: a healthy epithelium with sealed tight junctions. Bottom: a compromised barrier leaks LPS into circulation, raising IL-6, TNF-α and CRP and activating brain microglia.",
              credit: "Illustration: generated for Hikari Labs",
            },
            sections: [
              {
                heading: "Leaky gut, leaky brain",
                body: "When the gut barrier loosens, LPS — one of the most potent inflammatory triggers known — leaks into circulation.",
                diagram: {
                  type: "flow",
                  items: [
                    { icon: "🍷", label: "Trigger", note: "Stress, alcohol, NSAIDs, dysbiosis, low fibre" },
                    { icon: "🚪", label: "Tight junctions loosen", note: "Zonulin and claudin proteins disrupted" },
                    { icon: "🦠", label: "LPS leaks into blood", note: "Bacterial endotoxin from gram-negative bacteria" },
                    { icon: "🔥", label: "Systemic inflammation", note: "Innate immune activation; raises IL-6, TNF-α, CRP" },
                    { icon: "🧠", label: "Neuroinflammation", note: "Cytokines cross BBB; microglia activate; mood circuits affected" },
                  ],
                },
              },
              {
                heading: "The cytokine hypothesis of depression",
                body: "Three cytokines have the strongest evidence in depression. They activate IDO, depleting serotonin from below.",
                diagram: {
                  type: "cards",
                  items: [
                    { label: "IL-6", sub: "Interleukin-6", desc: "Most consistently elevated cytokine in depression. Activates IDO → depletes serotonin. Lowered by exercise, fibre, Mediterranean diet.", colorVar: "--danger" },
                    { label: "TNF-α", sub: "Tumour necrosis factor", desc: "Strongly inflammatory. Anti-TNF biologics (used for arthritis) have produced antidepressant effects in inflammation-positive depression.", colorVar: "--mod-3" },
                    { label: "CRP", sub: "C-reactive protein", desc: "Acute-phase reactant; routine blood marker. High CRP predicts non-response to SSRIs and better response to anti-inflammatories.", colorVar: "--mod-5" },
                  ],
                },
              },
              {
                heading: "The SMILES trial — food as treatment",
                body: "If gut inflammation drives depression, can food move the needle? Jacka et al. (BMC Medicine 2017) showed yes — by 4×.",
                diagram: {
                  type: "definition-box",
                  term: "Nutritional Psychiatry",
                  definition: "The field studying diet as a primary preventive and therapeutic intervention for mental illness — built largely on gut-brain mechanisms. SMILES trial: Mediterranean-style diet produced 32% vs 8% remission in moderate-severe depression at 12 weeks.",
                  key_enzyme: "Multiple — IDO, mTOR, NLRP3 inflammasome",
                  key_bacteria: ["Faecalibacterium prausnitzii (anti-inflammatory)", "Akkermansia muciniphila (barrier-protective)", "Bifidobacterium (LPS-lowering)"],
                },
              },
            ],
            flashcards: [
              { q: "What is LPS and how does it link gut health to mood?", a: "Lipopolysaccharide — a fragment of gram-negative bacterial cell walls. When the gut barrier leaks, LPS enters the blood and triggers systemic inflammation that reaches the brain." },
              { q: "Name the three cytokines most consistently elevated in depression.", a: "IL-6, TNF-α, and CRP." },
              { q: "What was the headline finding of the SMILES trial?", a: "A 12-week Mediterranean-style dietary intervention produced a 4× higher remission rate in moderate-to-severe depression compared to social-support control (32% vs 8%) — independent of weight change." },
              { q: "Why might high-CRP patients respond poorly to standard SSRIs?", a: "Their depression is more inflammation-driven. The kynurenine pathway is dominant, so simply boosting serotonin reuptake isn't enough — the underlying inflammation must also be addressed." },
            ],
          },
        },
        {
          id: "mb6.4",
          title: "HPA Axis Hijack — How Stress Reshapes the Microbiome",
          duration: "8 min",
          content: {
            intro:
              "The HPA axis — hypothalamus, pituitary, adrenals — is your central stress response. It runs in lockstep with the gut: stress changes the microbiome, the microbiome changes stress responsiveness.",
            heroImage: {
              src: "/diagrams/mb6-4-hpa-axis.png",
              alt: "Anatomical illustration of the HPA axis and its downstream effects on the gut",
              caption:
                "Hypothalamus → pituitary → adrenals → cortisol, with negative feedback to the hippocampus. Chronic activation loosens gut tight junctions and reduces beneficial Lactobacilli.",
              credit: "Illustration: generated for Hikari Labs",
            },
            sections: [
              {
                heading: "The HPA axis in 90 seconds",
                body: "Cortisol is meant to be a short, sharp signal. Chronic stress breaks the feedback that turns it off.",
                diagram: {
                  type: "pathway",
                  steps: [
                    { num: "1", label: "Stressor", body: "Real, imagined, social, or physiological. The brain interprets.", colorVar: "--mod-4" },
                    { num: "2", label: "Hypothalamus", body: "Releases CRH (corticotropin-releasing hormone).", colorVar: "--mod-6" },
                    { num: "3", label: "Pituitary", body: "Releases ACTH (adrenocorticotropic hormone) into the bloodstream.", colorVar: "--mod-1" },
                    { num: "4", label: "Adrenals", body: "Release cortisol (and adrenaline). Mobilises glucose, suppresses immunity, sharpens focus.", colorVar: "--mod-3" },
                    { num: "5", label: "Feedback", body: "Cortisol binds receptors in hippocampus and hypothalamus to shut the system down. Chronic stress erodes these receptors.", colorVar: "--danger" },
                  ],
                },
              },
              {
                heading: "How chronic stress reshapes the microbiome",
                body: "Cortisol is a permeability hormone. It reshapes which bacteria can survive in the gut.",
                diagram: {
                  type: "cause-web",
                  items: [
                    { icon: "🔓", label: "Loosens tight junctions", note: "Cortisol disrupts zonulin/occludin → leaky gut" },
                    { icon: "🩸", label: "Changes mucus layer", note: "Thinner mucus = bacteria contact epithelium directly" },
                    { icon: "🐢", label: "Slows motility", note: "Promotes overgrowth in small intestine (SIBO)" },
                    { icon: "📉", label: "Drops diversity", note: "Fewer species → less resilience" },
                    { icon: "🪫", label: "Reduces Lactobacilli", note: "Beneficial GABA/mood bacteria most stress-sensitive" },
                    { icon: "📈", label: "Pathogens rise", note: "Opportunistic species fill the niche" },
                    { icon: "🌙", label: "Disrupts circadian", note: "Microbiome runs on a 24h clock that cortisol entrains" },
                  ],
                },
              },
              {
                heading: "How the microbiome reshapes stress",
                body: "The loop runs both ways. Germ-free mice and probiotic fMRI studies prove it.",
                diagram: {
                  type: "two-column",
                  left: {
                    title: "What stress does to the gut",
                    colorVar: "--danger",
                    points: [
                      "Cortisol opens tight junctions → leaky gut",
                      "Reduces beneficial Lactobacillus & Bifidobacterium",
                      "Slows motility (SIBO risk)",
                      "Lowers microbial diversity",
                      "Thins protective mucus layer",
                    ],
                  },
                  right: {
                    title: "What the gut does to stress",
                    colorVar: "--mod-6",
                    points: [
                      "Healthy microbiome dampens HPA reactivity (Sudo 2004)",
                      "Specific strains calm fMRI emotional response (Tillisch 2013)",
                      "SCFAs (butyrate) reduce hippocampal inflammation",
                      "Vagal tone improves with probiotic intake",
                      "Tryptophan stays in serotonin pathway, not kynurenine",
                    ],
                  },
                },
              },
            ],
            flashcards: [
              { q: "Sketch the HPA axis in one sentence.", a: "Hypothalamus releases CRH → pituitary releases ACTH → adrenals release cortisol, with negative feedback from cortisol binding hippocampal and hypothalamic receptors." },
              { q: "Name three ways chronic cortisol reshapes the microbiome.", a: "(1) Loosens gut tight junctions, (2) reduces beneficial Lactobacilli and Bifidobacteria, (3) lowers overall microbial diversity. Also slows motility and thins mucus." },
              { q: "What did Sudo et al. (2004) demonstrate about germ-free mice and stress?", a: "Germ-free mice had hugely exaggerated HPA responses to mild stress. Colonising with Bifidobacterium infantis during a critical early window normalised the response — proving the microbiome programs stress reactivity." },
              { q: "What did Tillisch et al. (2013) show about probiotics in healthy humans?", a: "4 weeks of a multi-strain probiotic measurably reduced activity in brain regions involved in emotional reactivity, on fMRI — the first imaging proof of psychobiotic effect in humans." },
            ],
          },
        },
        {
          id: "mb6.5",
          title: "Estrogen × Mood × Microbes",
          duration: "9 min",
          content: {
            intro:
              "If you've taken the Hormone-Gut Axis course, this is where the two narratives merge. Mood swings in PMS, anxiety in perimenopause, postpartum depression — all sit at the intersection of estrogen, the microbiome and the brain.",
            heroImage: {
              src: "/diagrams/mb6-5-estrogen-cycle.png",
              alt: "Estrogen cycle curve combined with the estrobolome enterohepatic recycling loop",
              caption:
                "A normal 28-day estrogen curve and the estrobolome loop beneath it. β-glucuronidase-producing bacteria deconjugate estrogen in the colon, sending it back into circulation.",
              credit: "Illustration: generated for Hikari Labs",
            },
            sections: [
              {
                heading: "Estrogen is a mood molecule",
                body: "Estrogen directly modulates serotonin, dopamine and BDNF. Falling estrogen = falling serotonin tone.",
                diagram: {
                  type: "balance",
                  left: {
                    label: "Estrogen high or rising",
                    icon: "🌞",
                    desc: "Boosts serotonin synthesis, increases 5-HT2A receptor density, elevates BDNF, enhances dopamine. Mood-stabilising at physiological levels.",
                  },
                  right: {
                    label: "Estrogen falling",
                    icon: "🌒",
                    desc: "Drops serotonin tone, downregulates receptors, lowers BDNF. Predisposes to PMDD, postpartum depression, perimenopausal anxiety/depression.",
                  },
                },
              },
              {
                heading: "Where the microbiome enters",
                body: "When the estrobolome is balanced, estrogen cycling is smooth. When it's not, the cycle gets erratic — and so does mood.",
                diagram: {
                  type: "flow",
                  items: [
                    { icon: "🦠", label: "Estrobolome dysbiosis", note: "β-glucuronidase activity out of balance" },
                    { icon: "📈", label: "Erratic estrogen", note: "Sharper peaks and crashes across the cycle" },
                    { icon: "🧠", label: "Wider mood swings", note: "Serotonin and BDNF track the curve — bigger drops at withdrawal" },
                    { icon: "🔁", label: "PMDD / perimenopause", note: "The 'I don't recognise myself' weeks" },
                  ],
                },
              },
              {
                heading: "The reproductive transitions",
                body: "Three points in a woman's life carry the highest mood risk — all three are estrogen-withdrawal events.",
                diagram: {
                  type: "symptom-grid",
                  items: [
                    { system: "Late luteal phase (PMS / PMDD)", symptoms: ["Estrogen and progesterone both drop", "Serotonin tone falls", "Irritability, low mood, rage, anxiety in the 7–10 days before bleeding", "Higher β-glucuronidase = sharper crash"] },
                    { system: "Postpartum", symptoms: ["Estrogen drops ~100-fold within 48h of delivery", "Dramatic neurotransmitter shift", "Postpartum depression risk 10–15%", "Disrupted gut barrier from labour, antibiotics, sleep loss"] },
                    { system: "Perimenopause", symptoms: ["Estrogen oscillates wildly for 4–10 years before menopause", "Often more disruptive than menopause itself", "Anxiety often appears for the first time in life", "Microbiome shifts (Lactobacilli decline) compound it"] },
                    { system: "Hormonal contraceptives", symptoms: ["Synthetic hormones override the natural curve", "Some women experience low mood / loss of libido", "Changes the vaginal AND gut microbiome", "Mood symptoms most common in first 6 months"] },
                  ],
                },
              },
              {
                heading: "The integrative read",
                body: "Why 'normal' labs can still mean feeling terrible — labs miss the shape of the curve and the resilience of the systems metabolising it.",
                diagram: {
                  type: "two-column",
                  left: {
                    title: "What standard labs catch",
                    colorVar: "--mod-3",
                    points: [
                      "Estrogen level at one point in time",
                      "Total testosterone, total thyroid",
                      "TSH and standard reference ranges",
                      "Often labelled 'normal' even when symptoms are severe",
                    ],
                  },
                  right: {
                    title: "What functional/integrative labs add",
                    colorVar: "--mod-6",
                    points: [
                      "DUTCH panel — estrogen metabolites + cycle pattern",
                      "GI-MAP — β-glucuronidase activity, dysbiosis markers",
                      "Cortisol curve across the day, not a single value",
                      "Reads HPA, gut and hormones as one connected system",
                    ],
                  },
                },
              },
            ],
            flashcards: [
              { q: "Why does estrogen affect mood directly?", a: "It boosts serotonin synthesis, increases 5-HT2A receptor density, raises BDNF, and enhances dopamine signalling. When estrogen drops, all of these fall with it." },
              { q: "Name the three estrogen-withdrawal life events with highest mood risk.", a: "Late luteal phase (PMS/PMDD), postpartum (~100-fold estrogen drop in 48h), and perimenopause (years of wildly oscillating estrogen)." },
              { q: "What does the estrobolome have to do with mood swings?", a: "When β-glucuronidase activity is out of balance, estrogen cycling becomes erratic — sharper peaks and crashes. Bigger withdrawal = bigger mood symptoms." },
              { q: "Why might a woman feel terrible despite 'normal' hormone labs?", a: "Standard labs measure absolute levels at one moment. They miss the shape of her cycle, the metabolite ratios, and the resilience of the gut/microbiome buffering the curve." },
            ],
          },
        },
        {
          id: "mb6.6",
          title: "Psychobiotics, Food & the Mood Toolkit",
          duration: "8 min",
          content: {
            intro:
              "What actually moves the needle? Six tools have credible human-RCT evidence for mood: targeted probiotic strains, fermented foods, Mediterranean eating, fibre, omega-3s, and certain micronutrients. Strains matter — skip the generic 'probiotic' aisle.",
            heroImage: {
              src: "/diagrams/mb6-6-mediterranean-plate.png",
              alt: "Overhead illustration of a Mediterranean plate with fermented food jars alongside",
              caption:
                "The Mediterranean + psychobiotic plate behind the SMILES trial: oily fish, leafy greens, legumes, EVOO, nuts, berries — flanked by live fermented foods (yoghurt, kefir, sauerkraut, kimchi).",
              credit: "Illustration: generated for Hikari Labs",
            },
            sections: [
              {
                heading: "Strains with mood evidence",
                body: "Generic probiotics aren't psychobiotics. Buy by strain name, not by genus.",
                diagram: {
                  type: "probiotic-table",
                  items: [
                    { bacteria: "L. helveticus R0052 + B. longum R0175 (Cerebiome)", role: "Reduces anxiety and depression scores; lowers urinary cortisol (Messaoudi 2011, RCT)", source: "Garden of Life Dr. Formulated Mood+, Pure Encapsulations ProbioMood, others under licence" },
                    { bacteria: "Bifidobacterium longum 1714", role: "Lowers stress reactivity and cortisol in healthy adults (Allen 2016, double-blind crossover RCT)", source: "PrecisionBiotics Zenflore" },
                    { bacteria: "Lactobacillus rhamnosus JB-1", role: "Vagus-nerve-mediated anxiety reduction in animal models; human trials ongoing", source: "Research-grade; emerging commercial availability" },
                    { bacteria: "Bifidobacterium infantis 35624", role: "Reduces gut-derived inflammation; restores HPA function in animal models (Sudo lineage)", source: "Align (Procter & Gamble)" },
                    { bacteria: "Multi-strain (VSL#3 / Visbiome)", role: "Reduced anxiety in functional GI disorders; mixed mood data", source: "Visbiome (US/EU)" },
                  ],
                },
              },
              {
                heading: "Food: the daily psychobiotic",
                body: "The SMILES trial proved a Mediterranean diet rivals talking therapy for mild-moderate depression. The active ingredients aren't mysterious.",
                diagram: {
                  type: "food-grid",
                  categories: [
                    { title: "Eat daily ⭐", colorVar: "--mod-2", foods: ["30+ plant species per week", "Oily fish 2–3×/week (salmon, sardines, mackerel)", "Extra-virgin olive oil", "Mixed nuts & seeds", "Berries, leafy greens", "Whole grains (oats, barley)"], note: "Builds microbial diversity and supplies anti-inflammatory polyphenols + omega-3." },
                    { title: "Add fermented foods", colorVar: "--mod-6", foods: ["Live yoghurt (full-fat, unsweetened)", "Kefir", "Sauerkraut, kimchi (unpasteurised)", "Miso, tempeh, natto", "Kombucha (low-sugar)"], note: "Live cultures + bacterial metabolites. 4–6 servings per week (Sonnenburg lab, Cell 2021)." },
                    { title: "Mood-supportive micronutrients", colorVar: "--mod-1", foods: ["Eggs (choline, B12)", "Brazil nuts (selenium)", "Leafy greens (folate, magnesium)", "Pumpkin seeds (zinc, magnesium)", "Liver / lentils (iron, B vitamins)"], note: "Cofactors for serotonin synthesis and methylation." },
                    { title: "Reduce / avoid", colorVar: "--danger", foods: ["Ultra-processed foods", "Daily alcohol (>1 drink/day)", "Refined sugar / HFCS", "Industrial seed oils", "Routine sweeteners (sucralose disrupts microbiome)"], note: "All linked to lower microbial diversity and higher inflammation." },
                  ],
                },
              },
              {
                heading: "Supplements with evidence",
                body: "Cofactors, not substitutes. Test before treating where possible — B12, vitamin D, ferritin, magnesium deficiencies often masquerade as depression.",
                diagram: {
                  type: "supplement-list",
                  items: [
                    { name: "EPA omega-3 (1–2 g/day)", action: "Reduces neuroinflammation; high-EPA formulations have RCT evidence in depression (Mocking 2016 meta-analysis)", timing: "Daily with food" },
                    { name: "Vitamin D3 (test first; aim 30–50 ng/mL)", action: "Low D consistently associated with depression; supplementation helps the deficient subgroup most", timing: "Daily, with K2 for cardiovascular safety" },
                    { name: "Magnesium glycinate (200–400 mg)", action: "Cofactor for serotonin synthesis; calms HPA axis; widely deficient in Western diets", timing: "Evening" },
                    { name: "B-complex (active forms)", action: "Required for monoamine synthesis. MTHFR variants benefit most. Use methylfolate and methylcobalamin.", timing: "Morning" },
                    { name: "Targeted psychobiotic (Cerebiome R0052+R0175)", action: "Specific anxiety/depression evidence (Messaoudi 2011)", timing: "30+ day trial; reassess at 8 weeks" },
                    { name: "Saffron extract (Affron 28 mg/day)", action: "Multiple RCTs show effects comparable to low-dose SSRIs in mild-moderate depression", timing: "Daily; allow 4–6 weeks" },
                  ],
                },
              },
            ],
            flashcards: [
              { q: "Why is the strain name on a probiotic label important?", a: "Mood effects are strain-specific. 'Lactobacillus rhamnosus' tells you nothing — the JB-1 strain has anxiolytic data, generic L. rhamnosus doesn't." },
              { q: "Name the canonical psychobiotic combination from Messaoudi (2011).", a: "Lactobacillus helveticus R0052 + Bifidobacterium longum R0175. Commercially called Cerebiome." },
              { q: "What 4–6 weekly servings did the Sonnenburg lab (Cell 2021) show to increase microbial diversity?", a: "Fermented foods — kefir, kimchi, sauerkraut, yoghurt, kombucha, etc. Outperformed even a high-fibre arm in that trial." },
              { q: "Which omega-3 fraction has the best RCT evidence in depression?", a: "EPA (eicosapentaenoic acid), especially at 1–2 g/day and at high EPA:DHA ratios (Mocking 2016 meta-analysis)." },
            ],
          },
        },
        {
          id: "mb6.7",
          title: "Eastern Lenses — Ayurveda & Chinese Medicine on Mood",
          duration: "9 min",
          content: {
            intro:
              "Long before the gut-brain axis was a research field, two traditions had built sophisticated frameworks around it: Ayurveda's Agni and Manasika Prakriti, and TCM's Heart-Spleen connection. Modern microbiome research is, in many places, rediscovering what they already mapped.",
            heroImage: {
              src: "/diagrams/mb6-7-eastern-frameworks.png",
              alt: "Side-by-side illustration of Ayurvedic doshas with Agni and the TCM five-element cycle",
              caption:
                "Two traditional maps of the gut-mind connection. Left: the Ayurvedic doshas around Agni (digestive fire). Right: the TCM five elements, with the Heart-Spleen axis highlighted.",
              credit: "Illustration: generated for Hikari Labs",
            },
            sections: [
              {
                heading: "Ayurveda — Agni and the three mental qualities",
                body: "Ayurveda has held for ~2,500 years that mental health emerges from digestive fire.",
                diagram: {
                  type: "cards",
                  items: [
                    { label: "Sattva", sub: "Clarity, balance, lightness", desc: "Calm, steady mind. Supported by fresh whole foods, regular meals, moderate exercise, meditation, adequate sleep.", colorVar: "--mod-2" },
                    { label: "Rajas", sub: "Agitation, restlessness", desc: "Anxiety, overwhelm, racing thoughts. Aggravated by stimulants, irregular eating, overwork, sensory overload.", colorVar: "--mod-3" },
                    { label: "Tamas", sub: "Heaviness, dullness", desc: "Depression, fog, inertia. Aggravated by heavy/stale food, oversleeping, lack of movement, ultra-processed foods (Ama-producing).", colorVar: "--mod-4" },
                    { label: "Agni", sub: "Digestive fire", desc: "The central regulator. Strong Agni → Ojas (steady mind). Weak/erratic Agni → Ama → mental cloudiness or mood disorders.", colorVar: "--mod-5" },
                  ],
                },
              },
              {
                heading: "Chinese medicine — the Heart-Spleen relationship",
                body: "In TCM the Spleen governs digestion, the Heart houses the mind. They're linked — and worry is the two-way bridge.",
                diagram: {
                  type: "cards",
                  items: [
                    { label: "Spleen (Pi)", sub: "Earth element; digestion", desc: "Transforms food into Qi and Blood. Weakened by worry, cold raw food, irregular meals, overthinking. Maps loosely onto gut barrier + microbiome function.", colorVar: "--mod-3" },
                    { label: "Heart (Xin)", sub: "Fire element; houses Shen", desc: "The Shen — mind/spirit/consciousness — resides in the Heart. Calmed Shen = clear thought, restful sleep. Disturbed Shen = anxiety, insomnia, palpitations.", colorVar: "--mod-5" },
                    { label: "Heart-Spleen pattern", sub: "The classical mood diagnosis", desc: "Weak Spleen fails to nourish Heart Blood → unhoused Shen. Presents as fatigue + anxiety + poor sleep + palpitations + pale tongue. Treated with herbs like Gui Pi Tang.", colorVar: "--mod-6" },
                    { label: "Worry damages Spleen", sub: "The two-way street", desc: "Overthinking — the emotion of the Spleen — directly weakens digestion. Mirrors the modern observation that chronic stress reshapes the microbiome.", colorVar: "--mod-1" },
                  ],
                },
              },
              {
                heading: "What modern research validates",
                body: "Modern microbiome science keeps converging on what these traditions described centuries ago.",
                diagram: {
                  type: "two-column",
                  left: {
                    title: "Traditional observation",
                    colorVar: "--mod-3",
                    points: [
                      "Weak Agni → Ama → mental fog (Ayurveda)",
                      "Heart-Spleen disharmony → anxiety + fatigue (TCM)",
                      "Worry damages digestion (TCM)",
                      "Heavy/stale foods produce Tamas mood (Ayurveda)",
                      "Triphala restores Agni and mental clarity (Ayurveda)",
                    ],
                  },
                  right: {
                    title: "Modern correlate",
                    colorVar: "--mod-6",
                    points: [
                      "Dysbiosis + LPS → neuroinflammation → low mood",
                      "Gut dysbiosis + HPA dysregulation → anxiety + fatigue",
                      "Chronic stress reshapes microbiome (Sudo 2004)",
                      "Ultra-processed food → low diversity → depression (SMILES)",
                      "Triphala modulates microbiome composition (Peterson 2017)",
                    ],
                  },
                },
              },
              {
                heading: "How to actually use this",
                body: "You don't have to pick a side — Eastern systems for individualised pattern reading, Western tools for objective biomarkers.",
                diagram: {
                  type: "pathway",
                  steps: [
                    { num: "1", label: "Diagnose the pattern (Eastern)", body: "Constitution (Prakriti / element type), current imbalance (Sattva–Rajas–Tamas; Heart-Spleen pattern), digestive fire (Agni / Pi function).", colorVar: "--mod-3" },
                    { num: "2", label: "Confirm with biomarkers (Western)", body: "DUTCH for hormone curve, GI-MAP for dysbiosis + β-glucuronidase, blood inflammation markers (hs-CRP, IL-6).", colorVar: "--mod-6" },
                    { num: "3", label: "Treat the overlap", body: "Warming + lightly bitter cooked foods, regular meals, breath practices, targeted probiotic + nutrient support where labs show specific deficits.", colorVar: "--mod-2" },
                    { num: "4", label: "Reassess", body: "Track pattern shifts (clinical) AND biomarker trends (labs) together at 8–12 weeks. The two views should converge.", colorVar: "--mod-1" },
                  ],
                },
              },
            ],
            flashcards: [
              { q: "In Ayurveda, what is Agni and what happens when it's weak?", a: "Agni is the digestive fire — the central regulator of body and mind. Weak Agni produces Ama (undigested residue) which manifests as mental cloudiness, fatigue, low mood, or Tamas." },
              { q: "Define the Manasika Prakriti qualities Sattva, Rajas, and Tamas.", a: "Sattva = clarity and balance. Rajas = agitation, restlessness, anxiety. Tamas = heaviness, dullness, depression. Diet and Agni shift the balance." },
              { q: "Explain the TCM Heart-Spleen pattern in modern terms.", a: "Weak Spleen function (loosely: gut barrier and microbiome) fails to nourish the Heart, which houses the Shen (mind). The result is fatigue + anxiety + poor sleep + palpitations — closely resembling the modern gut-brain inflammation picture." },
              { q: "How does the Patwardhan (2014) framework integrate Ayurveda with modern science?", a: "It bridges Ayurvedic doctrine (Prakriti, Agni) with systems biology and the microbiome — treating the classical constitution types as biologically measurable subgroups rather than abstract categories." },
            ],
          },
        },
      ],
    },

    // -------------------------------------------------------------------
    // MODULE 7 — Restoration & Resilience (stub)
    // -------------------------------------------------------------------
    {
      id: 7,
      title: "Restoration & Resilience",
      icon: "🌱",
      colorVar: "--mod-7",
      summary:
        "Antibiotics, pre/pro/syn/postbiotics, Ayurvedic & TCM digestive support, the IFM 5R framework and GI-MAP testing.",
      comingSoon: true,
      lessons: [],
    },

    // -------------------------------------------------------------------
    // MODULE 8 — Microbiome × Body Systems (stub)
    // -------------------------------------------------------------------
    {
      id: 8,
      title: "Microbiome × Body Systems",
      icon: "🌐",
      colorVar: "--mod-8",
      summary:
        "Gut–skin, gut–metabolic, gut–cardiovascular, gut–immune. The body-system tour, anchored by Murray Ch 78.",
      comingSoon: true,
      lessons: [],
    },
  ],
};
